import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import AddRounded from '@mui/icons-material/AddRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import DragIndicatorRounded from '@mui/icons-material/DragIndicatorRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import UnfoldLessRounded from '@mui/icons-material/UnfoldLessRounded';
import UnfoldMoreRounded from '@mui/icons-material/UnfoldMoreRounded';

// ─── Recursive node shape ───
// Node = { title, label, mfg_price?, add_on?, children?: Node[] }
// If `children` is a non-empty array → group (no price inputs).
// Otherwise → priced row with optional MFG price + Add-on.

const LEVEL_COLORS = {
  1: { bg: '#EEF2FF', fg: '#4338CA' }, // indigo
  2: { bg: '#ECFDF5', fg: '#047857' }, // emerald
  3: { bg: '#FFF7ED', fg: '#C2410C' }, // orange
  4: { bg: '#FEF2F2', fg: '#B91C1C' }, // red
  5: { bg: '#F3E8FF', fg: '#7E22CE' }, // purple
};

const LevelTag = ({ level }) => {
  const c = LEVEL_COLORS[Math.min(level, 5)] || LEVEL_COLORS[5];
  return (
    <Box
      sx={{
        width: 28,
        height: 22,
        borderRadius: 0.75,
        bgcolor: c.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Typography sx={{ fontWeight: 700, color: c.fg, fontSize: '0.65rem' }}>
        L{level}
      </Typography>
    </Box>
  );
};

const LabeledInput = ({ label, value, onChange, type = 'text', sx, placeholder }) => (
  <Box sx={{ ...sx }}>
    {label && (
      <Typography sx={{ display: 'block', mb: 0.25, fontSize: '0.7rem', color: 'text.secondary' }}>
        {label}
      </Typography>
    )}
    <TextField
      size="small"
      type={type}
      value={value ?? ''}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
      InputProps={{ sx: { borderRadius: 1.5, fontSize: '0.85rem' } }}
      inputProps={{ style: { padding: '6px 10px' } }}
    />
  </Box>
);

// Helpers to mutate a deeply-nested children array via a path of indices
const updateAtPath = (groups, path, mutate) => {
  if (path.length === 0) return mutate(groups);
  const [head, ...rest] = path;
  return groups.map((node, i) => {
    if (i !== head) return node;
    return { ...node, children: updateAtPath(node.children || [], rest, mutate) };
  });
};

const PricingEditor = ({ value, onChange, computeListPrice }) => {
  const groups = value?.groups || [];

  const setGroups = (next) => onChange({ ...(value || {}), groups: next });

  // Collapse state — set of path-keys whose children are hidden. Lets you fold
  // the tree so a large pricing table isn't all open (and hard to edit safely).
  const [collapsed, setCollapsed] = useState(new Set());
  const toggleCollapse = (key) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  // Collapse to level 1 = fold every L1 group (its children hide → only L1 rows show).
  const collapseToL1 = () => setCollapsed(new Set(groups.map((_, i) => String(i))));
  const expandAll = () => setCollapsed(new Set());

  // path = array of indices from root to the parent whose children we mutate
  const addNodeAt = (path, level) => {
    setGroups(updateAtPath(groups, path, (children) => [
      ...children,
      { title: '', label: '', mfg_price: '', add_on: '', children: [] },
    ]));
  };

  const updateNodeAt = (path, patch) => {
    if (path.length === 0) return;
    const idx = path[path.length - 1];
    const parentPath = path.slice(0, -1);
    setGroups(updateAtPath(groups, parentPath, (children) =>
      children.map((c, i) => (i === idx ? { ...c, ...patch } : c))
    ));
  };

  const removeNodeAt = (path) => {
    if (path.length === 0) return;
    const idx = path[path.length - 1];
    const parentPath = path.slice(0, -1);
    setGroups(updateAtPath(groups, parentPath, (children) =>
      children.filter((_, i) => i !== idx)
    ));
  };

  // Duplicate a row/group: deep-copy it and insert the copy right below, as a
  // sibling. You then rename it and delete any one line you don't need.
  const duplicateNodeAt = (path) => {
    if (path.length === 0) return;
    const idx = path[path.length - 1];
    const parentPath = path.slice(0, -1);
    setGroups(updateAtPath(groups, parentPath, (children) => {
      const copy = JSON.parse(JSON.stringify(children[idx]));
      const next = [...children];
      next.splice(idx + 1, 0, copy);
      return next;
    }));
  };

  // ─── Drag-to-reorder among siblings (same parent only) ───
  // drag = { key: parentPath joined, from: index }. A node only drops onto a
  // sibling under the same parent so the tree structure stays intact.
  const [drag, setDrag] = useState(null);
  const [overKey, setOverKey] = useState(null); // `${parentKey}:${idx}` being hovered
  const moveNode = (parentPath, from, to) => {
    if (from === to) return;
    setGroups(updateAtPath(groups, parentPath, (children) => {
      const next = [...children];
      const [m] = next.splice(from, 1);
      next.splice(to, 0, m);
      return next;
    }));
  };

  // Convert a priced row → group by adding an empty child
  const promoteToGroup = (path) => {
    setGroups(updateAtPath(groups, path.slice(0, -1), (children) =>
      children.map((c, i) => {
        if (i !== path[path.length - 1]) return c;
        const next = { ...c, children: [{ title: '', label: '', mfg_price: c.mfg_price, add_on: c.add_on, children: [] }] };
        next.mfg_price = '';
        next.add_on = '';
        return next;
      })
    ));
  };

  const renderNode = (node, path) => {
    const level = path.length;
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const list = !hasChildren && computeListPrice ? computeListPrice(node) : null;
    const idx = path[path.length - 1];
    const parentPath = path.slice(0, -1);
    const parentKey = parentPath.join('-') || 'root';
    const cellKey = `${parentKey}:${idx}`;
    const pathKey = path.join('-');
    const isCollapsed = hasChildren && collapsed.has(pathKey);
    const isDragOver = overKey === cellKey && drag && drag.key === parentKey && drag.from !== idx;

    return (
      <Box
        key={path.join('-')}
        onDragOver={(e) => {
          if (!drag || drag.key !== parentKey) return; // only siblings
          e.preventDefault();
          if (overKey !== cellKey) setOverKey(cellKey);
        }}
        onDrop={(e) => {
          if (!drag || drag.key !== parentKey) return;
          e.preventDefault();
          moveNode(parentPath, drag.from, idx);
          setDrag(null);
          setOverKey(null);
        }}
        sx={{
          mb: 1.25,
          ...(level === 1
            ? { border: '1px solid', borderColor: isDragOver ? 'primary.main' : 'divider', borderRadius: 2, p: 1.5 }
            : { borderLeft: '2px solid', borderColor: isDragOver ? 'primary.main' : 'divider', pl: 1.5 }),
          opacity: drag && drag.key === parentKey && drag.from === idx ? 0.5 : 1,
          transition: 'border-color 0.12s, opacity 0.12s',
        }}
      >
        {/* Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: hasChildren ? 1 : 0 }}>
          <Box
            draggable
            onDragStart={(e) => {
              setDrag({ key: parentKey, from: idx });
              e.dataTransfer.effectAllowed = 'move';
              try { e.dataTransfer.setData('text/plain', String(idx)); } catch { /* Safari */ }
            }}
            onDragEnd={() => { setDrag(null); setOverKey(null); }}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, width: 22, height: 26, borderRadius: 1,
              color: 'text.secondary', bgcolor: 'action.hover',
              cursor: 'grab', '&:hover': { bgcolor: 'action.selected', color: 'text.primary' },
              '&:active': { cursor: 'grabbing' },
            }}
            title="Drag to reorder"
          >
            <DragIndicatorRounded sx={{ fontSize: 18 }} />
          </Box>
          {hasChildren ? (
            <IconButton
              size="small"
              onClick={() => toggleCollapse(pathKey)}
              sx={{ flexShrink: 0, p: 0.25 }}
              aria-label={isCollapsed ? 'Expand' : 'Collapse'}
            >
              <ChevronRightRounded sx={{ fontSize: 18, transform: isCollapsed ? 'none' : 'rotate(90deg)', transition: 'transform .15s' }} />
            </IconButton>
          ) : (
            <Box sx={{ width: 24, flexShrink: 0 }} />
          )}
          <LevelTag level={level} />
          <TextField
            size="small"
            placeholder={level === 1 ? 'L1 title (e.g. Batoo Bench - 2 Seats)' : 'Title'}
            value={node.title || ''}
            onChange={(e) => updateNodeAt(path, { title: e.target.value })}
            sx={{ flex: 1.4, minWidth: 0 }}
            InputProps={{ sx: { borderRadius: 1.5, fontSize: '0.85rem', fontWeight: level === 1 ? 600 : 400 } }}
            inputProps={{ style: { padding: '6px 10px' } }}
          />
          <TextField
            size="small"
            placeholder="Label"
            value={node.label || ''}
            onChange={(e) => updateNodeAt(path, { label: e.target.value })}
            sx={{ flex: 1, minWidth: 0 }}
            InputProps={{ sx: { borderRadius: 1.5, fontSize: '0.85rem', color: 'primary.main' } }}
            inputProps={{ style: { padding: '6px 10px' } }}
          />
          {!hasChildren && (
            <>
              <Tooltip title="Add sub-level">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => promoteToGroup(path)}
                  sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: '0.7rem', px: 1, py: 0.5, minWidth: 0 }}
                >
                  + sub-level
                </Button>
              </Tooltip>
              <TextField
                size="small"
                type="number"
                placeholder="MFG"
                value={node.mfg_price ?? ''}
                onChange={(e) => updateNodeAt(path, { mfg_price: e.target.value })}
                sx={{ width: 90 }}
                InputProps={{ sx: { borderRadius: 1.5, fontSize: '0.85rem' } }}
                inputProps={{ style: { padding: '6px 10px', textAlign: 'right' } }}
              />
              <TextField
                size="small"
                type="number"
                placeholder="Add-on"
                value={node.add_on ?? ''}
                onChange={(e) => updateNodeAt(path, { add_on: e.target.value })}
                sx={{ width: 90 }}
                InputProps={{ sx: { borderRadius: 1.5, fontSize: '0.85rem' } }}
                inputProps={{ style: { padding: '6px 10px', textAlign: 'right' } }}
              />
              <Box sx={{ width: 80, textAlign: 'right' }}>
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: list != null ? 'primary.main' : 'text.disabled',
                  }}
                >
                  {list != null
                    ? `$${list.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : '—'}
                </Typography>
              </Box>
            </>
          )}
          <Tooltip title="Duplicate">
            <IconButton
              size="small"
              onClick={() => duplicateNodeAt(path)}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
            >
              <ContentCopyRounded sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={() => removeNodeAt(path)}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
          >
            <CloseRounded sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        {/* Collapsed hint */}
        {isCollapsed && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 4, fontStyle: 'italic' }}>
            {node.children.length} sub-row{node.children.length === 1 ? '' : 's'} hidden — click ▸ to expand
          </Typography>
        )}

        {/* Children */}
        {hasChildren && !isCollapsed && (
          <Box sx={{ ml: 3, mt: 1 }}>
            {node.children.map((child, ci) => renderNode(child, [...path, ci]))}
            <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddRounded sx={{ fontSize: 14 }} />}
                onClick={() => addNodeAt(path, level + 1)}
                sx={{ borderRadius: 99, textTransform: 'none', fontSize: '0.7rem', py: 0.4 }}
              >
                Add L{level + 1} row
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  if (groups.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 1.5 }}>
          No pricing groups yet.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddRounded sx={{ fontSize: 16 }} />}
          onClick={() => addNodeAt([], 1)}
          sx={{ borderRadius: 99, textTransform: 'none', fontSize: '0.8rem' }}
        >
          Add L1 group
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Collapse controls */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
        <Button size="small" startIcon={<UnfoldLessRounded sx={{ fontSize: 16 }} />} onClick={collapseToL1}
          sx={{ textTransform: 'none', fontSize: '0.72rem' }}>
          Collapse to L1
        </Button>
        <Button size="small" startIcon={<UnfoldMoreRounded sx={{ fontSize: 16 }} />} onClick={expandAll}
          sx={{ textTransform: 'none', fontSize: '0.72rem' }}>
          Expand all
        </Button>
      </Box>

      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5, mb: 1 }}>
        <Box sx={{ width: 22 }} />
        <Box sx={{ width: 28 }} />
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', flex: 1.4 }}>Title</Typography>
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', flex: 1 }}>Label</Typography>
        <Box sx={{ width: 78 }} />
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', width: 90, textAlign: 'right' }}>MFG price</Typography>
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', width: 90, textAlign: 'right' }}>Add-on</Typography>
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', width: 80, textAlign: 'right' }}>List price</Typography>
        <Box sx={{ width: 32 }} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {groups.map((group, gi) => renderNode(group, [gi]))}
      </Box>

      <Button
        variant="outlined"
        startIcon={<AddRounded sx={{ fontSize: 16 }} />}
        onClick={() => addNodeAt([], 1)}
        sx={{ borderRadius: 99, textTransform: 'none', fontSize: '0.8rem', mt: 1 }}
      >
        Add L1 group
      </Button>
    </Box>
  );
};

// ─── Helper to flatten the tree into rows for preview ───
export const flattenPricing = (groups, computeListPrice) => {
  const out = [];
  const walk = (node, path) => {
    if (node.children && node.children.length > 0) {
      const breadcrumb = [...path, node.title || node.label].filter(Boolean);
      node.children.forEach((c) => walk(c, breadcrumb));
    } else {
      const breadcrumb = [...path, node.title || node.label].filter(Boolean);
      const list = computeListPrice ? computeListPrice(node) : null;
      out.push({
        path: breadcrumb,
        label: breadcrumb.join(' / '),
        mfg_price: parseFloat(node.mfg_price) || 0,
        add_on: parseFloat(node.add_on) || 0,
        list_price: list,
      });
    }
  };
  groups.forEach((g) => walk(g, []));
  return out;
};

export default PricingEditor;
